import React, { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { Modal } from "@/components/common/Modal";
import { Input } from "@/components/common/Input";
import { Button } from "@/components/common/Button";
import { Permission, CreateRoleDTO, Application } from "@/contracts/auth-api.types";
import { roleService } from "@/services/roleService";
import { useNotification } from "@/hooks/useNotification";
import { getErrorMessage } from "@/utils/formatters";
import { Search } from "lucide-react";

const roleSchema = z.object({
  name: z
    .string()
    .min(1, "Nome da role é obrigatório")
    .max(50, "Nome da role muito longo")
    .regex(/^[A-Z0-9_-]+$/, "Nome da role deve ser UPPERCASE (ex: ADMIN, MANAGER, OPERATOR)"),
  description: z.string().max(255).optional(),
  application_id: z.string().optional(),
});

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultApplicationId?: string;
  applications?: Application[];
}

const EMPTY_APPLICATIONS: Application[] = [];

export const RoleModal: React.FC<RoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultApplicationId,
  applications = EMPTY_APPLICATIONS,
}) => {
  const { notify } = useNotification();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [applicationId, setApplicationId] = useState(defaultApplicationId || "");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permSearch, setPermSearch] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function loadPermissions() {
      try {
        const list = await roleService.listPermissions(undefined, applicationId || undefined);
        setPermissions(list || []);
      } catch {
        // Fallback silencioso
      }
    }
    if (isOpen) {
      void loadPermissions();
    }
  }, [isOpen, applicationId]);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setApplicationId(defaultApplicationId || applications[0]?.id || "");
      setSelectedPermissionIds([]);
      setPermSearch("");
      setErrors({});
    }
  }, [isOpen, defaultApplicationId, applications]);

  const tenantPermissions = useMemo(() => {
    if (!applicationId) return permissions;
    return permissions.filter((p) => !p.application_id || p.application_id === applicationId);
  }, [permissions, applicationId]);

  const filteredPermissions = useMemo(() => {
    if (!permSearch.trim()) return tenantPermissions;
    const term = permSearch.toLowerCase();
    return tenantPermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term)),
    );
  }, [tenantPermissions, permSearch]);

  const togglePermission = (id: string) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const handleSelectAllFiltered = () => {
    const ids = filteredPermissions.map((p) => p.id);
    setSelectedPermissionIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handleDeselectAll = () => {
    setSelectedPermissionIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const formattedName = name.trim().toUpperCase();
    const result = roleSchema.safeParse({
      name: formattedName,
      description,
      application_id: applicationId,
    });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateRoleDTO = {
        name: formattedName,
        description: description || undefined,
        permission_ids: selectedPermissionIds,
        ...(applicationId ? { application_id: applicationId } : {}),
      };
      await roleService.createRole(payload);
      notify(`Role "${formattedName}" criada com sucesso!`, "success");
      onSuccess();
      onClose();
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao criar role"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cadastrar Nova Role RBAC" maxWidth="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {applications.length > 0 && (
          <div className="space-y-1">
            <label htmlFor="role-tenant-select" className="input-label">
              Tenant / Aplicação Vinculada
            </label>
            <select
              id="role-tenant-select"
              className="input-control"
              value={applicationId}
              onChange={(e) => {
                setApplicationId(e.target.value);
                setSelectedPermissionIds([]);
              }}
              disabled={isSubmitting}
            >
              <option value="">Aplicação Atual (Default)</option>
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name} ({app.client_id})
                </option>
              ))}
            </select>
          </div>
        )}

        <Input
          id="role-name-input"
          label="Nome da Role (UPPERCASE)"
          value={name}
          onChange={(e) => setName(e.target.value.toUpperCase())}
          error={errors.name}
          placeholder="Ex: AUDITOR, SUPPORT, MANAGER"
          helperText="Convenção canônica em maiúsculas"
          required
        />

        <Input
          id="role-description-input"
          label="Descrição do Perfil"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={errors.description}
          placeholder="Ex: Acesso somente leitura aos módulos de relatório"
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="input-label mb-0">
              Permissões Iniciais ({selectedPermissionIds.length} selecionadas)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSelectAllFiltered}
                className="text-xs text-primary hover:underline font-medium"
              >
                Marcar todas
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="text-xs text-muted hover:underline font-medium"
              >
                Limpar
              </button>
            </div>
          </div>

          {permissions.length > 6 && (
            <div className="search-input-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Filtrar permissões..."
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
                className="search-input text-xs py-1.5"
              />
            </div>
          )}

          {tenantPermissions.length === 0 ? (
            <p className="text-xs text-muted p-2.5 bg-gray-50 rounded border border-gray-200">
              {applicationId
                ? "Nenhuma permissão cadastrada para o catálogo deste tenant."
                : "Carregando catálogo de permissões..."}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md max-h-52 overflow-y-auto">
              {filteredPermissions.map((perm) => (
                <label
                  key={perm.id}
                  className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer transition-colors ${
                    selectedPermissionIds.includes(perm.id)
                      ? "bg-blue-50 text-blue-900 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPermissionIds.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                    className="rounded text-primary focus:ring-primary h-4 w-4"
                  />
                  <span className="font-mono">{perm.name}</span>
                </label>
              ))}
              {filteredPermissions.length === 0 && (
                <div className="col-span-full py-3 text-center text-xs text-muted">
                  Nenhuma permissão com o termo pesquisado.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Criar Role
          </Button>
        </div>
      </form>
    </Modal>
  );
};
